/* eslint-disable no-underscore-dangle */
import React, { FC, PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Form, Formik, FormikProps } from 'formik';
import { styled } from 'linaria/react';
import { CommissionDataByServiceIdType } from '@entities/denomination';
import { SteamAccountPayee, useCyberStore } from '@entities/tsp';
import { PaymentFormValues, PaymentMethod } from '@entities/payment';
import { getSteamAccountFinalAmount } from '@entities/price';
import { PROMOCODES_FROM_2K, WIDGET_PROMOODE } from '@entities/promocode';
import { steamQrShowGtm } from '@shared/lib/gtm/redesign';
import { useCurrentTspOnly } from '@shared/lib';
import { Partners, usePartner } from '@shared/lib/partner';
import { Amount } from '@root/api/ump-ewallet';
import { getClientTotalAmount } from '../lib/getClientTotalAmount';
import { useGetCommissions } from '../model/useGetCommissions';
import { usePayBySBP } from '../model/usePayBySBP';
import { usePayByMtsAccount } from '../model/usePayByMtsAccount';
import { getSteamPaymentFormValidation } from '../config/steamPaymentFormValidation';
import { initialPaymentFormValues } from '../config/form';
import { useRequestId } from '../lib/useRequestId';
export interface TspInfoProps {
  title: string;
  icon?: string;
  isVouchers?: boolean;
  params?: Array<{
    name: string;
    description?: string;
  }>;
  activeService?: SteamAccountPayee;
}
export interface PaymentFormProps {
  payee: SteamAccountPayee;
  getCommissions: (amount: Required<Pick<Amount, 'base' | 'fee' | 'total'>>, serviceId?: string) => void;
  isPaymentloading: boolean;
  isAmountInfoLoading: boolean;
}
interface SbpQrModalProps {
  payee: SteamAccountPayee;
  open: boolean;
  handleClose: () => void;
  qrLink: string;
  description: string;
}
interface Props {
  h1: string;
  payee: SteamAccountPayee;
  commissionData: CommissionDataByServiceIdType;
  renderTspInfo: (props: TspInfoProps) => ReactNode;
  renderStories: () => ReactNode;
  renderPromo: () => ReactNode;
  renderPaymentForm: (props: PaymentFormProps) => ReactNode;
  renderHelp: () => ReactNode;
  renderQrModal: (props: SbpQrModalProps) => ReactNode;
}
const SteamPaymentForm: FC<PropsWithChildren<Props>> = ({
  h1,
  payee,
  commissionData,
  renderTspInfo,
  renderStories,
  renderPromo,
  renderPaymentForm,
  renderHelp,
  renderQrModal
}) => {
  const isVouchers = false;
  const router = useRouter();
  const paymentFormRef = useRef<FormikProps<PaymentFormValues>>(null);
  const {
    partner
  } = usePartner();
  const {
    isCurrentTspOnly
  } = useCurrentTspOnly();
  const {
    paymentMethod,
    isPaymentloading,
    isAmountInfoLoading,
    setIsPromocodeActivated,
    lastActivatedPromocode,
    requestID,
    pcekMdOrder,
    amountInfo,
    isPromocodeActivated
  } = useCyberStore();
  const [id, setId] = useRequestId();
  const {
    getCommissions,
    isGetPaymentTermsLoading,
    cancelGetPaymentTermsRequest
  } = useGetCommissions({
    isVouchers,
    payee,
    paymentFormRef,
    commissionData
  });
  const {
    qrLink,
    qrcId,
    payBySBP,
    cancelSBPPayment
  } = usePayBySBP({
    isVouchers,
    payee,
    id,
    setId
  });
  const {
    payByMtsAccount,
    isPaymentByMtsAccountLoading
  } = usePayByMtsAccount({
    isVouchers,
    payee
  });
  useEffect(() => {
    if (paymentFormRef.current?.values?.price && paymentMethod === PaymentMethod.MTS_ACCOUNT) {
      cancelGetPaymentTermsRequest();
      getCommissions(getSteamAccountFinalAmount(Number(paymentFormRef.current?.values?.price)));
    }
  }, [paymentMethod]);
  const getPayParams = (transactionId = pcekMdOrder) => {
    const price = paymentFormRef.current?.values.price || '';
    const amount = String(getClientTotalAmount(amountInfo, isPromocodeActivated, true) || price).replace(/\s/g, '');
    return {
      amount,
      requestId: requestID || id,
      transactionId,
      paymentMethod
    };
  };
  const handleServiceFormSubmitData = (values: PaymentFormValues) => {
    console.log(values);
    if (isAmountInfoLoading) return;
    if (values.promocode.toUpperCase() === WIDGET_PROMOODE && Number(values.price) < 2500) {
      setIsPromocodeActivated(false);
      paymentFormRef.current?.setFieldError('promocode', 'Условия не выполнены');
      return;
    }
    if (PROMOCODES_FROM_2K?.includes(lastActivatedPromocode) && Number(values.price) < 2000) {
      setIsPromocodeActivated(false);
      paymentFormRef.current?.setFieldError('promocode', 'Промокод действует только при пополнении от 2.000 ₽');
      return;
    }
    const isFromPubgGiveawayPage = router.query.from === 'rozygrysh-prizov-pubg-mobile';
    if (isFromPubgGiveawayPage) {
      window.recordTier?.push({
        eventName: 'landingpageClick',
        tracking_id: '128635520'
      });
      window._tmr?.push({
        type: 'reachGoal',
        id: 3712315,
        goal: 'click_purchase'
      });
    }
    if (partner === Partners.DEFAULT) {
      window._tmr?.push({
        type: 'reachGoal',
        id: 3712315,
        goal: 'click_pay_games_cyber'
      });
    }
    if (paymentMethod === PaymentMethod.SBP) {
      payBySBP(values);
    } else {
      payByMtsAccount();
    }
  };
  useEffect(() => {
    if (!qrLink || paymentMethod !== PaymentMethod.SBP) {
      return;
    }
    steamQrShowGtm(getPayParams(qrcId || pcekMdOrder));
    // qrLink — момент показа QR, остальные поля читаются из текущего рендера
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrLink]);
  return <Formik enableReinitialize validateOnChange={false} innerRef={paymentFormRef} initialValues={initialPaymentFormValues} validationSchema={getSteamPaymentFormValidation({
    partner,
    paymentMethod
  })} onSubmit={handleServiceFormSubmitData}>
      <StyledForm>
        <ContentWrapper isCurrentTspOnly={isCurrentTspOnly}>
          <TspInfoWrapper>
            {renderTspInfo({
            title: h1,
            icon: payee?.features?.tsp_picture_web,
            isVouchers,
            params: payee?.params,
            activeService: payee
          })}
          </TspInfoWrapper>
          {!isCurrentTspOnly && <StoriesAndPromoWrapper>
              <StoriesWrapper>{renderStories()}</StoriesWrapper>
              <PromoWrapper>{renderPromo()}</PromoWrapper>
            </StoriesAndPromoWrapper>}
          <PaymentFormWrapper>
            {renderPaymentForm({
            payee,
            getCommissions,
            isPaymentloading: isPaymentloading || isPaymentByMtsAccountLoading,
            isAmountInfoLoading: isAmountInfoLoading || isGetPaymentTermsLoading
          })}
          </PaymentFormWrapper>
          <HelpWrapper>{renderHelp()}</HelpWrapper>
        </ContentWrapper>
        {renderQrModal({
        payee,
        open: !!qrLink && paymentMethod === PaymentMethod.SBP,
        qrLink,
        description: h1,
        handleClose: cancelSBPPayment
      })}
      </StyledForm>
    </Formik>;
};
export default SteamPaymentForm;
const _exp = /*#__PURE__*/() => Form;
const StyledForm = /*#__PURE__*/styled(_exp())({
  name: "StyledForm",
  class: "s131bhvn",
  propsAsIs: true
});
const _exp2 = /*#__PURE__*/() => ({
  isCurrentTspOnly
}) => isCurrentTspOnly ? "'c c d'" : "'b c d'";
const _exp3 = /*#__PURE__*/() => ({
  isCurrentTspOnly
}) => isCurrentTspOnly ? `
      'a'
      'c'
      'd'
    ` : `
      'a'
      'c'
      'd'
      'b'
    `;
const ContentWrapper = /*#__PURE__*/styled('div')({
  name: "ContentWrapper",
  class: "c1m9gkzw",
  propsAsIs: false,
  vars: {
    "c1m9gkzw-0": [_exp2()],
    "c1m9gkzw-1": [_exp3()]
  }
});
const TspInfoWrapper = /*#__PURE__*/styled('section')({
  name: "TspInfoWrapper",
  class: "txc7w3l",
  propsAsIs: false
});
const StoriesAndPromoWrapper = /*#__PURE__*/styled('div')({
  name: "StoriesAndPromoWrapper",
  class: "sg9cxi4",
  propsAsIs: false
});
const StoriesWrapper = /*#__PURE__*/styled('section')({
  name: "StoriesWrapper",
  class: "scm57am",
  propsAsIs: false
});
const PromoWrapper = /*#__PURE__*/styled('section')({
  name: "PromoWrapper",
  class: "p1lkbinp",
  propsAsIs: false
});
const PaymentFormWrapper = /*#__PURE__*/styled('section')({
  name: "PaymentFormWrapper",
  class: "pgx71ze",
  propsAsIs: false
});
const HelpWrapper = /*#__PURE__*/styled('section')({
  name: "HelpWrapper",
  class: "h5d9y0q",
  propsAsIs: false
});

require("./SteamPaymentForm.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./SteamPaymentForm.tsx");